import { createStore } from 'vuex'
import { getUserInfo } from '@/utils/auth' // Import your auth-related functions
import { jwtDecode } from 'jwt-decode'

let notificationCounter = 0

export default createStore({
  state: {
    sidebarVisible: '',
    sidebarUnfoldable: false,
    theme: 'default',
    accessToken: null,
    targetRoute: '/dashboard',
    user: null,
    overriddenLocale: null,
    permissions: null, // Use null to distinguish between "not loaded" and "empty array"
    authenticationComplete: false, // Track when authentication flow is complete
    notifications: [],
  },
  mutations: {
    addNotification(state, notification) {
      state.notifications.push(notification)
    },
    removeNotification(state, id) {
      state.notifications = state.notifications.filter((n) => n.id !== id)
    },
    toggleSidebar(state) {
      state.sidebarVisible = !state.sidebarVisible
    },
    toggleTheme(state, payload) {
      state.theme = payload.value
    },
    toggleUnfoldable(state) {
      state.sidebarUnfoldable = !state.sidebarUnfoldable
    },
    updateSidebarVisible(state, payload) {
      state.sidebarVisible = payload.value
    },
    setAccessToken(state, token) {
      state.accessToken = token
    },
    setUser(state, user) {
      state.user = user
    },
    setTargetRoute(state, targetRoute) {
      state.targetRoute = targetRoute
    },
    setOverriddenLocale(state, locale) {
      state.overriddenLocale = locale
    },
    clearOverriddenLocale(state) {
      state.overriddenLocale = null
    },
    setPermissions(state, permissions) {
      // New mutation for setting permissions
      state.permissions = permissions
    },
    setAuthenticationComplete(state, complete) {
      state.authenticationComplete = complete
    },
  },
  getters: {
    getAccessToken: (state) => state.accessToken,
    getTargetRoute: (state) => state.targetRoute,
    currentLocale: (state) => state.overriddenLocale || userLocale,
    getPermissions: (state) => state.permissions || [],
    
    // Enhanced permission checking with hierarchical support
    hasPermission: (state) => (permission) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 [PERMISSION CHECK] Checking permission: ${permission}`)
        console.log(`📋 [USER PERMISSIONS] User has permissions:`, state.permissions)
      }
      
      if (!state.permissions) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`❌ [PERMISSION CHECK] No permissions loaded, denying access`)
        }
        return false
      }
      
      // Direct permission check
      if (state.permissions.includes(permission)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ [PERMISSION CHECK] Direct permission match for: ${permission}`)
        }
        return true
      }
      
      // Check for parent permissions that might grant this permission
      // This is a simplified client-side hierarchy check
      const permissionHierarchy = {
        // Section permissions grant access to all features within that section
        'section_admin_view': ['feature_users_view', 'feature_roles_view', 'feature_permissions_view', 'feature_properties_view'],
        'section_operations_view': ['feature_transactions_current_view', 'feature_transactions_closed_view'],
        'section_configuration_view': ['feature_chargepoints_view', 'feature_vehicles_view', 'feature_idtokens_view'],
        
        // Feature manage permissions include view permissions
        'feature_users_manage': ['feature_users_view', 'action_users_create', 'action_users_update', 'action_users_delete'],
        'feature_roles_manage': ['feature_roles_view', 'action_roles_create', 'action_roles_update', 'action_roles_delete'],
        'feature_permissions_manage': ['feature_permissions_view'],
        'feature_properties_manage': ['feature_properties_view', 'action_properties_create', 'action_properties_update', 'action_properties_delete'],
        'feature_chargepoints_manage': ['feature_chargepoints_view', 'action_chargepoints_create', 'action_chargepoints_update', 'action_chargepoints_delete'],
        'feature_vehicles_manage': ['feature_vehicles_view', 'action_vehicles_create', 'action_vehicles_update', 'action_vehicles_delete'],
        'feature_idtokens_manage': ['feature_idtokens_view', 'action_idtokens_create', 'action_idtokens_update', 'action_idtokens_delete'],
        
        // Action permissions require corresponding feature permissions  
        'action_users_create': ['feature_users_manage', 'feature_users_view'],
        'action_users_update': ['feature_users_manage', 'feature_users_view'],
        'action_users_delete': ['feature_users_manage', 'feature_users_view'],
        'action_users_reset_password': ['feature_users_manage', 'feature_users_view'],
        
        'action_roles_create': ['feature_roles_manage', 'feature_roles_view'],
        'action_roles_update': ['feature_roles_manage', 'feature_roles_view'],
        'action_roles_delete': ['feature_roles_manage', 'feature_roles_view'],
        'action_roles_assign': ['feature_roles_manage', 'feature_roles_view'],
        
        'action_chargepoints_create': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        'action_chargepoints_update': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        'action_chargepoints_delete': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        'action_chargepoints_start': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        'action_chargepoints_stop': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        'action_chargepoints_reset': ['feature_chargepoints_manage', 'feature_chargepoints_view'],
        
        // Transaction export permissions
        'action_transactions_current_export_csv': ['feature_transactions_current_view'],
        'action_transactions_current_export_excel': ['feature_transactions_current_view'],
        'action_transactions_closed_export_csv': ['feature_transactions_closed_view'],
        'action_transactions_closed_export_excel': ['feature_transactions_closed_view'],
        
        'action_vehicles_create': ['feature_vehicles_manage', 'feature_vehicles_view'],
        'action_vehicles_update': ['feature_vehicles_manage', 'feature_vehicles_view'],
        'action_vehicles_delete': ['feature_vehicles_manage', 'feature_vehicles_view'],
        
        'action_idtokens_create': ['feature_idtokens_manage', 'feature_idtokens_view'],
        'action_idtokens_update': ['feature_idtokens_manage', 'feature_idtokens_view'],
        'action_idtokens_delete': ['feature_idtokens_manage', 'feature_idtokens_view'],
        
        'action_properties_create': ['feature_properties_manage', 'feature_properties_view'],
        'action_properties_update': ['feature_properties_manage', 'feature_properties_view'],
        'action_properties_delete': ['feature_properties_manage', 'feature_properties_view']
      }
      
      // Expand permissions based on hierarchy
      const expandPermission = (perm) => {
        const result = new Set([perm])
        const queue = [perm]
        
        while (queue.length > 0) {
          const current = queue.shift()
          const children = permissionHierarchy[current] || []
          
          children.forEach(child => {
            if (!result.has(child)) {
              result.add(child)
              queue.push(child)
            }
          })
        }
        
        return Array.from(result)
      }
      
      // Check if any user permission grants the requested permission through hierarchy
      for (const userPerm of state.permissions) {
        const expandedPerms = expandPermission(userPerm)
        if (process.env.NODE_ENV === 'development') {
          console.log(`🔄 [HIERARCHY CHECK] ${userPerm} expands to:`, expandedPerms)
        }
        if (expandedPerms.includes(permission)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ [PERMISSION CHECK] Hierarchical permission match: ${userPerm} grants ${permission}`)
          }
          return true
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`❌ [PERMISSION CHECK] No permission match for: ${permission}`)
      }
      return false
    },
    
    hasAnyPermission: (state, getters) => (permissions) => {
      return permissions.some(permission => getters.hasPermission(permission))
    },
    
    hasAllPermissions: (state, getters) => (requiredPermissions) => {
      if (!state.permissions) return false
      return requiredPermissions.every(permission => getters.hasPermission(permission))
    },
    
    // Convenience getters for common permission patterns
    canViewAdminPages: (state, getters) => getters.hasPermission('section_admin_view'),
    canViewOperationsPages: (state, getters) => getters.hasPermission('section_operations_view'),
    canViewConfigurationPages: (state, getters) => getters.hasPermission('section_configuration_view'),
    
    canViewUsers: (state, getters) => getters.hasPermission('feature_users_view'),
    canManageUsers: (state, getters) => getters.hasPermission('feature_users_manage'),
    canCreateUsers: (state, getters) => getters.hasPermission('action_users_create'),
    canEditUsers: (state, getters) => getters.hasPermission('action_users_update'),
    canDeleteUsers: (state, getters) => getters.hasPermission('action_users_delete'),
    
    canViewChargepoints: (state, getters) => getters.hasPermission('feature_chargepoints_view'),
    canManageChargepoints: (state, getters) => getters.hasPermission('feature_chargepoints_manage'),
    
    canViewTransactions: (state, getters) => getters.hasPermission('feature_transactions_view'),
    canExportTransactions: (state, getters) => getters.hasPermission('feature_transactions_export'),
    
    isAuthenticationComplete: (state) => state.authenticationComplete,
  },
  actions: {
    async logout({ commit }) {
      try {
        commit('setAccessToken', null)
        commit('setUser', null)
        commit('setPermissions', null)
        commit('setAuthenticationComplete', false)
      } catch (error) {
        // Handle logout error
      }
    },
    async checkUserIdentity({ commit, state }) {
      if (state.accessToken) {
        const response = await getUserInfo()
        if (response.success) {
          commit('setUser', response.result)
          // Don't set permissions here - they should come from JWT token
        } else {
          commit('setUser', null)
          commit('setPermissions', null)
        }
      }
    },
    async login({ commit, dispatch }, { accessToken }) {
      try {
        commit('setAccessToken', accessToken)
        const decodedToken = jwtDecode(accessToken)
        commit('setPermissions', decodedToken.permissions || [])
        await dispatch('checkUserIdentity') // Fetch user info and permissions after login
      } catch (error) {
        // Handle login error
      }
    },
    // Components across the app already dispatched this - it was simply never
    // implemented, so every call was a silent no-op ("unknown action type").
    showNotification({ commit }, { message, type = 'info', duration = 5000 }) {
      const id = `${Date.now()}-${notificationCounter++}`
      commit('addNotification', { id, message, type })

      if (duration > 0) {
        setTimeout(() => commit('removeNotification', id), duration)
      }

      return id
    },
    dismissNotification({ commit }, id) {
      commit('removeNotification', id)
    },
    setLocale({ commit }, locale) {
      commit('setOverriddenLocale', locale)
    },
    clearLocale({ commit }) {
      commit('clearOverriddenLocale')
    },
  },
  modules: {},
})
